/**
 * TTS 适配器注册表（T10a）。
 *
 * 与 `src/core/providers/registry.ts` 同构：
 * - 按 `TtsConfig.kind` 实例化适配器；
 * - 支持运行时注册 / 覆盖自定义适配器；
 * - 对未注册的 kind 抛出明确错误，而不是静默降级。
 *
 * 设计约定：注册表只持有「工厂函数」，不持有配置与密钥，
 * 因此同一个注册表可服务任意多个 TtsConfig 实例。
 */

import { createOpenAICompatibleTtsAdapter } from './openai';
import type {
  ResolvedTtsConfig,
  TtsAdapter,
  TtsAdapterFactory,
  TtsConfig,
  TtsDeps,
  TtsKind,
} from './types';

export const BUILTIN_TTS_KINDS: readonly TtsKind[] = ['openai-compatible', 'custom'] as const;

/**
 * 注册表 API 接受的 kind 参数：任意字符串（支持自定义 TTS 供应商），
 * 同时保留内置 kind 的字面量补全。
 *
 * 注意：不能写成 `TtsKind | string`——该联合会被 `string` 完全吸收、等价于 `string`，
 * 属于冗余类型分量（no-redundant-type-constituents 会报错）。这里用
 * `TtsKind | (string & {})`：`string & {}` 让联合类型保持可辨识，
 * 既保留字面量补全，又仍接受任意字符串。
 */
export type TtsKindInput = TtsKind | (string & {});

/** 未注册 kind 时抛出；上层可捕获后提示用户配置。 */
export class TtsNotRegisteredError extends Error {
  readonly kind: string;

  constructor(kind: string) {
    super(`没有为 tts kind "${kind}" 注册适配器`);
    this.name = 'TtsNotRegisteredError';
    this.kind = kind;
  }
}

export class TtsRegistry {
  private readonly factories = new Map<string, TtsAdapterFactory>();

  constructor(options?: { withDefaults?: boolean }) {
    if (options?.withDefaults !== false) {
      this.registerDefaults();
    }
  }

  /** 注册内置适配器。`custom` 默认走 OpenAI 兼容协议。 */
  registerDefaults(): this {
    this.register('openai-compatible', createOpenAICompatibleTtsAdapter);
    this.register('custom', createOpenAICompatibleTtsAdapter);
    return this;
  }

  /** 注册（或覆盖）某个 kind 的适配器工厂。 */
  register(kind: TtsKindInput, factory: TtsAdapterFactory): this {
    if (typeof factory !== 'function') {
      throw new TypeError(`tts 工厂必须是函数，收到 ${typeof factory}`);
    }
    this.factories.set(kind, factory);
    return this;
  }

  /** 注销某个 kind。 */
  unregister(kind: TtsKindInput): boolean {
    return this.factories.delete(kind);
  }

  has(kind: TtsKindInput): boolean {
    return this.factories.has(kind);
  }

  /** 已注册的 kind 列表（便于设置页展示与排错）。 */
  kinds(): string[] {
    return [...this.factories.keys()];
  }

  /** 按配置实例化适配器。 */
  create(config: ResolvedTtsConfig, deps?: TtsDeps): TtsAdapter {
    const factory = this.factories.get(config.kind);
    if (!factory) {
      throw new TtsNotRegisteredError(config.kind);
    }
    return factory(config, deps);
  }

  /** 批量实例化，跳过 `enabled === false` 的配置。 */
  createEnabled(configs: readonly ResolvedTtsConfig[], deps?: TtsDeps): TtsAdapter[] {
    return configs.filter((config) => config.enabled).map((config) => this.create(config, deps));
  }
}

/** 进程内默认注册表（内置 kind 已注册）。 */
export const defaultTtsRegistry = new TtsRegistry();

/** 便捷函数：用默认注册表实例化。 */
export function createTtsAdapter(config: ResolvedTtsConfig, deps?: TtsDeps): TtsAdapter {
  return defaultTtsRegistry.create(config, deps);
}

/**
 * 归一化外部配置：补齐必填字段并保证 `enabled` 为布尔值。
 * 用于从设置页 / 存储层读取到的宽松对象。
 */
export function normalizeTtsConfig(
  config: TtsConfig & Partial<Pick<ResolvedTtsConfig, 'apiKey' | 'headers'>>,
): ResolvedTtsConfig {
  return {
    ...config,
    name: config.name || config.kind,
    baseUrl: config.baseUrl,
    apiKeyRef: config.apiKeyRef,
    enabled: config.enabled ?? true,
  };
}
